import {Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy} from '@angular/core';
import {Subscription, timer} from 'rxjs';
import {debounceTime, take} from 'rxjs/operators';
import {LocalStorageManager} from 'src/app/data/manager/local-storage/local-storage.manager';
import {countryConfig} from 'src/country-config/country-config';
import {algoliaInit, userTokenAlgolia} from 'src/app/ui/algolia/algoliaInit';
import {ProductInterface} from '../../../../data/networking/models/shared/responses/product-response';
import {UpdateProductQuantityEvent} from '../../event-listeners/update-product-quantity/update-product-quantity.event';
import {DeleteProductFromCartEvent} from '../../event-listeners/delete-product-from-cart/delete-product-from-cart.event';
import {CartVm} from '../../../../view-model/cart/cart.vm';
import {CartDataUtil} from '../../utilities/cart-data/cart-data.util';
import {PlatformDetectionService} from 'src/app/ui/shared/services/detected-platform/platform-detection.service';

@Component({
  selector: 'app-quantity-controls',
  templateUrl: './product-quantity-controls.component.html',
  styleUrls: ['./product-quantity-controls.component.scss'],
})
export class ProductQuantityControlsComponent implements OnInit, OnChanges, OnDestroy {
  static inx = 0;

  @Input() product: ProductInterface;
  @Input() quantity = 0;
  @Input() isCard = false;
  @Input() isHome = false;
  @Input() isMiniCard = false;
  @Input() isLorealDesign = false;
  @Input() isVTO = false;
  @Input() idPrefix = '';
  @Input() customClasses = '';
  @Input() sas = false;
  @Input() queryID = '';
  @Input() positions = '';
  @Input() clear = false;
  @Input() isFlashOffer = false;
  @Input() isSearchCards = false;
  @Input() isMobile = false;
  @Input() removeItemInOne = false;
  @Output() update = new EventEmitter<Object>();
  active = false;
  model = {quantity: 0, active:false};
  preprocessor = 'product';
  gtmList = 'Carrito de compras';
  subscriptions: any = {};
  subscribeAndSave = false;
  index = ++ProductQuantityControlsComponent.inx;
  title = 'Agregar';
  flashOffer = '';
  searchCards = '';
  controlClose: Subscription;
  subscriptionList: Subscription[] = [];
  updatedAgileCartSubscription: Subscription;

  get isSampling(): boolean {
    return this.product?.rms_class?.toUpperCase() === 'SAMPLING';
  }

  constructor(
    private updateQuantityEvent: UpdateProductQuantityEvent,
    private deleteProductEvent: DeleteProductFromCartEvent,
    private cartVm: CartVm,
    private lsm: LocalStorageManager,
    private cartDataUtil: CartDataUtil,
    private platformDetectionService: PlatformDetectionService,
  ) {}

  algoliaEvent() {
    const user = this.lsm.get('customer')?.analyticsUUID || userTokenAlgolia;
    const id = this.product?.id;
    const platform = this.platformDetectionService.detectPlatform();

    if (this.queryID) {
      algoliaInit('addedToCartObjectIDsAfterSearch', {
        userToken: userTokenAlgolia,
        authenticatedUserToken: user,
        eventName: 'Product Added to Cart After Search',
        index: countryConfig.algolia.index,
        objectIDs: [`${id}`],
        queryID: this.queryID,
        source: 'WEB',
        objectData: [{
          price: this.product?.primePrice || this.product?.fullPrice,
          quantity: 1,
          discount: this.product?.offerPrice,
        }],
        value: this.product?.fullPrice,
        currency: 'COP',
        analyticsTags: [platform],
      });

      this.updateQuantityEvent.sendQueryID({queryID: this.queryID});
      this.cartDataUtil.storeProduct(id, this.queryID);
    } else {
      algoliaInit('addedToCartObjectIDs', {
        userToken: userTokenAlgolia,
        authenticatedUserToken: user,
        eventName: 'Product Added to Cart',
        index: countryConfig.algolia.index,
        objectIDs: [`${id}`],
        source: 'WEB',
        objectData: [{
          price: this.product?.primePrice || this.product?.fullPrice,
          quantity: 1,
          discount: this.product?.offerPrice,
        }],
        value: this.product?.fullPrice,
        currency: 'COP',
        analyticsTags: [platform],
      });
    }
  }

  add(id?) {
    if (this.isSampling) return;
    if (this.isCard) {
      this.algoliaEvent();

      const user = this.lsm.get('customer');
      const data = {
        idCustomerWebSafe: user?.idCustomerWebSafe,
        token: user?.token?.token,
        tokenIdWebSafe: user?.token?.tokenIdWebSafe,
        profileId: user?.analyticsUUID ? user?.analyticsUUID : 'anonymous',
        type: 'Added_to_cart',
        attributes: {
          prime_price: this.product?.primePrice,
          brand: this.product?.marca,
          discount: this.product?.discount ? this.product.discount : 0,
          item_category: this.product?.categorie,
          item_category2: this.product?.subCategory,
          item_department: this.product?.departments ? this.product?.departments[0] : 'none',
          item_id: id,
          item_name: this.product?.description,
          item_name_first_word: this.product?.description?.charAt(0),
          item_price: this.product?.fullPrice,
          item_quantity: 1,
          item_rms_class: this.product?.rms_class,
          item_rms_deparment: this.product?.rms_deparment,
          item_rms_group: this.product?.rms_group,
          item_rms_subclass: this.product?.rms_subclass,
        },
        responseContent: ['triggeredCampaigns', 'customerProfile'],
      };
      this.cartVm.sendEventTalonOne(data).subscribe();
    } else {
      this.algoliaEvent();
    }

    if (this.model.quantity < 1 || this.model.active === true) {
      this.updateQuantity('add');
    } else {
      this.model.active = true;
    }
  }

  remove() {
    this.updateQuantity('remove');
  }

  input() {
    this.updateQuantity('input');
  }

  delete() {
    this.deleteProductEvent.changeProductToDelete({
      product: this.product,
      subscribeAndSave: this.subscribeAndSave,
    });
  }

  updateQuantity(action) {
    if (this.product?.fullPrice) {
      if (this.checkUpdateQuantity()) {
        let {quantity} = this.model;
        switch (action) {
          case 'add':
            quantity += 1;
            break;
          case 'remove':
            quantity -= 1;
            break;
          default:
            timer(1)
              .pipe(take(1))
              .subscribe(() => {
                quantity = Number.isNaN(quantity) ? this.product.quantitySold : quantity;
              });
            break;
        }

        quantity = quantity >= this.product.totalStock ? this.product.totalStock : quantity;
        quantity = quantity <= 0 ? ((this.isCard || this.removeItemInOne) ? 0 : 1) : quantity;
        this.model = {quantity, active: true};
        if (this.model.quantity === 0) {
          this.delete();
          return;
        }
        timer(10)
          .pipe(take(1), debounceTime(100))
          .subscribe(() => {
            action = action === 'remove' ? 'update' : 'add';
            if (this.isCard) {
              if (!!this.model.quantity || this.model.quantity === 0) {
                this.updateQuantityEvent.changeProductQuantity({product: {...this.product}, quantity: this.model.quantity, action});
              }
            } else {
              this.update.emit(this.model.quantity);
            }
            this.controlClose?.unsubscribe();
            this.controlClose=timer(2000)
            .pipe(take(1), debounceTime(2000))
            .subscribe(() => {
              this.model.active=false;
            });
          })
      }
    }
  }

  verifySas() {
    if (this.product?.outofstore) {
      this.product.totalStock = 0;
    }
  }

  ngOnInit() {
    this.setModelQuantity();
    this.verifySas();
    this.setClassCartBtn();
    this.listenUpdatedAgileCart();
  }

  ngOnChanges(): void {
    if (this.product) {
      this.verifySas();
    }
  }

  ngOnDestroy(): void {
    this.subscriptionList?.forEach(subscription => subscription?.unsubscribe());
    this.controlClose?.unsubscribe();
  }

  sendId(product: ProductInterface): string{
    return product.objectID ? product.objectID : `${product.id}26`
  }

  setClassCartBtn(){
    this.flashOffer = this.isFlashOffer ? 'flash-offer ' : '';
    this.searchCards = this.isSearchCards ? 'app-button__search-cards ' : ''
  }
  shouldShowQuantityControls():boolean{
   return !this.product.handleQuantity && this.product.totalStock && !this.isSampling;
  }

  disableInputQuantity():boolean{
    const customClasses = this.customClasses === 'product-detail';
    const productStock = this.product.totalStock;
    const coupon = this.product.coupon;
    return customClasses || coupon || !productStock || this.isSampling;
  }
  checkUpdateQuantity():boolean{
    return (!this.product.coupon && this.product.totalStock > 0) || this.product.fullPrice !== 0;
  }
  disableAddQuantity():boolean{
    return this.product.totalStock === this.model.quantity || this.product.coupon;
  }

  listenUpdatedAgileCart(): void {
    this.updatedAgileCartSubscription = this.cartVm.updatedAgileCart()
      .subscribe(cart => {
        if (!cart) { return; }

        const productId = String(this.product?.id);
        const productInCart = cart?.itemList?.find(item => String(item?.id) === productId);

        if (productInCart) {
          this.product = {...this.product, quantitySold: productInCart?.quantitySold};
          this.setModelQuantity();
          this.updatedAgileCartSubscription?.unsubscribe();
        }
      });
    this.subscriptionList?.push(this.updatedAgileCartSubscription);
  }

  setModelQuantity(): void {
    const soldOut = 0;
    const quantity = this.product?.quantitySold || this.quantity || soldOut;
    this.model = {
      quantity,
      active: false,
    };
  }
}
